// frontend/src/context/SEOContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSEOSettings } from '../services/cmsService';

const SEOContext = createContext();

export const useSEO = () => useContext(SEOContext);

// صفحاتی که سئوی اختصاصی دارند (خودشان متا را مدیریت می‌کنند)
const CUSTOM_SEO_PATHS = [
  '/products/',
  '/blog/',
  '/packages/',
];

export const SEOProvider = ({ children }) => {
  const location = useLocation();
  const [currentSEO, setCurrentSEO] = useState(null);
  const [hasCustomSEO, setHasCustomSEO] = useState(false);
  
  const { data: settings } = useQuery({
    queryKey: ['seoSettings'],
    queryFn: getSEOSettings,
    staleTime: 3600000,
    retry: 1,
  });

  useEffect(() => {
    const path = location.pathname;
    const hasCustom = CUSTOM_SEO_PATHS.some(p => path.startsWith(p));
    setHasCustomSEO(hasCustom);
    
    if (!hasCustom) {
      setCurrentSEO(getDefaultSEO(path, settings));
    } else {
      setCurrentSEO(null);
    }
  }, [location.pathname, settings]);

  const getDefaultSEO = (path, settings) => {
    // صفحه اصلی
    if (path === '/') {
      return {
        title: settings?.default_meta_title || 'سان ایران | فروشگاه تخصصی تجهیزات خورشیدی',
        description: settings?.default_meta_description || 'خرید آنلاین پنل خورشیدی، اینورتر، باتری و تجهیزات نیروگاه خورشیدی با بهترین قیمت',
        noindex: settings?.block_search_engines || false,
      };
    }
    
    // درباره ما
    if (path === '/about') {
      return {
        title: 'درباره ما | سان ایران',
        description: 'آشنایی با سان ایران، بزرگترین بازار آنلاین تجهیزات خورشیدی در ایران',
        noindex: false,
      };
    }
    
    // تماس با ما
    if (path === '/contact') {
      return {
        title: 'تماس با ما | سان ایران',
        description: 'راه‌های ارتباطی با تیم پشتیبانی سان ایران',
        noindex: false,
      };
    }
    
    // لیست بلاگ
    if (path === '/blog') {
      return {
        title: 'بلاگ | سان ایران',
        description: 'جدیدترین مقالات و اخبار دنیای انرژی خورشیدی | آموزش نصب و راه‌اندازی نیروگاه خورشیدی',
        noindex: false,
      };
    }
    
    // لیست محصولات
    if (path === '/products') {
      return {
        title: 'محصولات خورشیدی | سان ایران',
        description: 'خرید انواع پنل خورشیدی، اینورتر، باتری و تجهیزات جانبی با بهترین قیمت',
        noindex: false,
      };
    }
    
    // لیست پکیج‌ها
    if (path === '/packages') {
      return {
        title: 'پکیج‌های خورشیدی | سان ایران',
        description: 'خرید پکیج‌های آماده نیروگاه خورشیدی شامل پنل، اینورتر و تجهیزات نصب',
        noindex: false,
      };
    }
    
    // لیست نصاب‌ها
    if (path === '/installers') {
      return {
        title: 'لیست نصاب‌های خورشیدی | سان ایران',
        description: 'بهترین نصاب‌های سیستم‌های خورشیدی در سراسر ایران با ضمانت اجرا',
        noindex: false,
      };
    }
    
    // صفحات ادمین و پنل کاربری
    if (path.startsWith('/admin') || path.startsWith('/profile') || 
        path.startsWith('/seller/') || path.startsWith('/installer/')) {
      return {
        title: 'پنل مدیریت | سان ایران',
        description: 'مدیریت حساب کاربری، سفارشات و محصولات',
        noindex: true,
      };
    }
    
    // پیش‌فرض
    return {
      title: settings?.default_meta_title || 'سان ایران',
      description: settings?.default_meta_description || 'فروشگاه تخصصی تجهیزات خورشیدی',
      noindex: settings?.block_search_engines || false,
    };
  };

  const value = {
    seo: currentSEO,
    hasCustomSEO,
    setCustomSEO: (seoData) => {
      setCurrentSEO(seoData);
      setHasCustomSEO(true);
    },
    clearCustomSEO: () => {
      setHasCustomSEO(false);
      setCurrentSEO(null);
    },
  };

  return (
    <SEOContext.Provider value={value}>
      {children}
    </SEOContext.Provider>
  );
};